import { useState } from 'react';
import { Modal, Button, Stack, Group, Chip, NumberInput, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCoin } from '@tabler/icons-react';
import { useApiClient } from '../../api';

// Contract §4.15: 300 ≤ amount_cents ≤ 50000 ($3–$500).
const QUICK_AMOUNTS = [5, 10, 20, 50];
const MIN_DOLLARS = 3;
const MAX_DOLLARS = 500;
const RANGE_MESSAGE = `Tips must be between $${MIN_DOLLARS} and $${MAX_DOLLARS}`;

// Modal to send a one-off tip (POST /api/v1/sellers/{id}/tip-checkout,
// contract §4.15). On success the server returns a CCBill checkout URL and we
// leave the SPA entirely — the webhook completes the tip.
//
// `redirect` is injectable because jsdom's window.location.assign is
// LegacyUnforgeable (can't be spied/stubbed) — tests pass a spy; production
// uses the default.
const TipModal = ({
  opened,
  onClose,
  sellerId,
  displayName,
  redirect = (url) => window.location.assign(url),
}) => {
  const api = useApiClient();
  const [dollars, setDollars] = useState(QUICK_AMOUNTS[0]);
  const [submitting, setSubmitting] = useState(false);

  const amount = typeof dollars === 'number' ? dollars : parseFloat(dollars);
  const valid = Number.isFinite(amount) && amount >= MIN_DOLLARS && amount <= MAX_DOLLARS;

  const handleClose = () => {
    setDollars(QUICK_AMOUNTS[0]);
    onClose();
  };

  const handleSend = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const { url } = await api.post(`/api/v1/sellers/${sellerId}/tip-checkout`, {
        amount_cents: Math.round(amount * 100),
      });
      redirect(url); // keep the button loading while the browser navigates away
    } catch (err) {
      let message = err?.message || 'Could not start the tip checkout';
      if (err?.code === 'config') message = "Payments aren't set up yet";
      else if (err?.code === 'invalid') message = RANGE_MESSAGE;
      notifications.show({ title: 'Error', message, color: 'red' });
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Send a tip to ${displayName || 'this princess'}`}
      centered
      transitionProps={{ duration: 0 }}
    >
      <Stack gap="md">
        <Chip.Group
          value={QUICK_AMOUNTS.includes(amount) ? String(amount) : null}
          onChange={(value) => setDollars(Number(value))}
        >
          <Group gap="xs">
            {QUICK_AMOUNTS.map((quick) => (
              <Chip key={quick} value={String(quick)} color="yellow" variant="light">
                ${quick}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
        <NumberInput
          label="Amount (USD)"
          value={dollars}
          onChange={setDollars}
          min={MIN_DOLLARS}
          max={MAX_DOLLARS}
          decimalScale={2}
          prefix="$"
          error={!valid && dollars !== '' ? RANGE_MESSAGE : null}
        />
        <Text size="xs" c="dimmed">
          Tips are anonymous — the princess never sees who sent one.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="light" color="gray" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="yellow"
            leftSection={<IconCoin size="1rem" />}
            onClick={handleSend}
            disabled={!valid}
            loading={submitting}
          >
            Send tip
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default TipModal;
